import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useLanguage } from '../../hooks/useLanguage';
import { getTranslation } from '../../translations/presentation';

const particleFloat = keyframes`
  0%, 100% {
    transform: translateY(0) translateX(0) scale(1);
    opacity: 0.4;
  }
  50% {
    transform: translateY(-40px) translateX(30px) scale(1.2);
    opacity: 1;
  }
`;

const hexagonFloat = keyframes`
  0%, 100% {
    transform: translateY(0) rotate(0deg);
    opacity: 0.15;
  }
  50% {
    transform: translateY(-25px) rotate(180deg);
    opacity: 0.4;
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 
      0 0 40px rgba(100, 182, 255, 0.3),
      0 0 80px rgba(0, 255, 255, 0.15),
      0 0 120px rgba(100, 182, 255, 0.1),
      inset 0 0 40px rgba(100, 182, 255, 0.08);
  }
  50% {
    box-shadow: 
      0 0 60px rgba(100, 182, 255, 0.5),
      0 0 120px rgba(0, 255, 255, 0.3),
      0 0 180px rgba(100, 182, 255, 0.2),
      inset 0 0 60px rgba(100, 182, 255, 0.15);
  }
`;

const scanline = keyframes`
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(100vh);
    opacity: 0;
  }
`;

const cornerExpand = keyframes`
  0%, 100% {
    width: 50px;
    height: 50px;
    opacity: 0.7;
  }
  50% {
    width: 70px;
    height: 70px;
    opacity: 1;
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const dataFlowLeft = keyframes`
  0% {
    transform: translateX(100%);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateX(-100%);
    opacity: 0;
  }
`;

const dataFlowRight = keyframes`
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const hologramFlicker = keyframes`
  0%, 100% {
    opacity: 1;
    filter: brightness(1);
  }
  50% {
    opacity: 0.95;
    filter: brightness(1.1);
  }
  75% {
    opacity: 0.98;
    filter: brightness(1.05);
  }
`;

const perspectivePulse = keyframes`
  0%, 100% {
    transform: perspective(1000px) rotateX(0deg) rotateY(0deg) translateZ(0px);
  }
  25% {
    transform: perspective(1000px) rotateX(2deg) rotateY(-2deg) translateZ(10px);
  }
  75% {
    transform: perspective(1000px) rotateX(-2deg) rotateY(2deg) translateZ(10px);
  }
`;

const energyPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.3;
  }
  50% {
    transform: scale(1.5);
    opacity: 0;
  }
`;

const textReveal = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
    filter: blur(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
`;

const quantumPulse = keyframes`
  0%, 100% {
    transform: scale(1) rotate(0deg);
    opacity: 0.8;
  }
  25% {
    transform: scale(1.05) rotate(1deg);
    opacity: 1;
  }
  50% {
    transform: scale(0.95) rotate(-1deg);
    opacity: 0.9;
  }
  75% {
    transform: scale(1.02) rotate(0.5deg);
    opacity: 1;
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 40px;
  overflow: hidden;
  position: relative;
  perspective: 3000px;
  cursor: pointer;
  user-select: none;
  background: radial-gradient(ellipse at 50% 50%, rgba(0, 20, 40, 0.4) 0%, rgba(0, 5, 15, 0.8) 100%);
  
  &:active {
    transform: scale(0.998);
  }
`;

const TechGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(100, 182, 255, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(100, 182, 255, 0.02) 1px, transparent 1px),
    radial-gradient(circle at 30% 40%, rgba(0, 255, 255, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 70% 60%, rgba(100, 182, 255, 0.03) 0%, transparent 50%);
  background-size: 80px 80px, 80px 80px, 100% 100%, 100% 100%;
  pointer-events: none;
  z-index: 1;
  opacity: 0.8;
`;

const Scanline = styled.div`
  position: absolute;
  top: -100%;
  left: 0;
  right: 0;
  height: 150px;
  background: linear-gradient(
    to bottom,
    transparent,
    rgba(0, 255, 255, 0.15),
    rgba(100, 182, 255, 0.1),
    transparent
  );
  pointer-events: none;
  animation: ${css`${scanline}`} 10s linear infinite;
  z-index: 2;
  filter: blur(2px);
`;

const FloatingParticles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 2;
`;

const Particle = styled.div`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  background: ${props => props.color};
  border-radius: 50%;
  animation: ${css`${particleFloat}`} ${props => props.duration}s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
  top: ${props => props.top}%;
  left: ${props => props.left}%;
  opacity: 0.4;
  filter: blur(1px);
  box-shadow: 0 0 10px ${props => props.color};
`;

const HexagonLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
`;

const Hexagon = styled.div`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size * 1.15}px;
  top: ${props => props.top}%;
  left: ${props => props.left}%;
  animation: ${css`${hexagonFloat}`} ${props => props.duration}s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: transparent;
    border: 1px solid ${props => props.color};
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  }
`;

const EnergyRing = styled.div`
  position: absolute;
  width: 400px;
  height: 400px;
  border: 2px solid rgba(0, 255, 255, 0.3);
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 1;
  animation: ${css`${energyPulse}`} 4s ease-in-out infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border: 2px solid rgba(100, 182, 255, 0.2);
    border-radius: 50%;
    animation: ${css`${energyPulse}`} 4s ease-in-out infinite 0.5s;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border: 2px solid rgba(0, 255, 255, 0.15);
    border-radius: 50%;
    animation: ${css`${energyPulse}`} 4s ease-in-out infinite 1s;
  }
  
  @media (max-width: 768px) {
    width: 250px;
    height: 250px;
  }
`;

const ContentPreview = styled.div`
  max-width: 1000px;
  width: 80%;
  margin: 0 auto;
  padding: 80px 60px;
  border: 1px solid rgba(100, 182, 255, 0.3);
  border-radius: 32px;
  background: linear-gradient(135deg, rgba(0, 10, 25, 0.95) 0%, rgba(0, 5, 15, 0.98) 100%);
  backdrop-filter: blur(60px) saturate(180%);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 3;
  animation: ${css`${glowPulse}`} 8s ease-in-out infinite;
  overflow: hidden;
  transform-style: preserve-3d;
  box-shadow: 
    0 30px 90px rgba(0, 0, 0, 0.7),
    0 0 120px rgba(100, 182, 255, 0.15),
    0 0 200px rgba(0, 255, 255, 0.08),
    inset 0 0 100px rgba(100, 182, 255, 0.03);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(100, 182, 255, 1),
      rgba(0, 255, 255, 1),
      rgba(100, 182, 255, 1),
      transparent
    );
    background-size: 200% 100%;
    animation: ${css`${gradientShift}`} 3s linear infinite;
    box-shadow: 0 0 25px rgba(0, 255, 255, 1), 0 5px 30px rgba(0, 255, 255, 0.5);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(0, 255, 255, 1),
      rgba(100, 182, 255, 1),
      rgba(0, 255, 255, 1),
      transparent
    );
    background-size: 200% 100%;
    animation: ${css`${gradientShift}`} 3s linear infinite reverse;
    box-shadow: 0 0 25px rgba(100, 182, 255, 1), 0 -5px 30px rgba(100, 182, 255, 0.5);
  }
  
  @media (max-width: 768px) {
    padding: 50px 30px;
  }
`;

const Corner = styled.div`
  position: absolute;
  width: 50px;
  height: 50px;
  border-color: rgba(0, 255, 255, 0.9);
  border-style: solid;
  animation: ${css`${cornerExpand}`} 3s ease-in-out infinite;
  filter: drop-shadow(0 0 10px rgba(0, 255, 255, 0.6));
  
  ${props => props.position === 'top-left' && css`
    top: -2px;
    left: -2px;
    border-width: 3px 0 0 3px;
    
    &::before {
      content: '';
      position: absolute;
      top: -5px;
      left: -5px;
      width: 15px;
      height: 15px;
      background: rgba(0, 255, 255, 0.3);
      border-radius: 50%;
      box-shadow: 0 0 15px rgba(0, 255, 255, 0.8);
    }
  `}
  
  ${props => props.position === 'top-right' && css`
    top: -2px;
    right: -2px;
    border-width: 3px 3px 0 0;
    animation-delay: 0.75s;
    
    &::before {
      content: '';
      position: absolute;
      top: -5px;
      right: -5px;
      width: 15px;
      height: 15px;
      background: rgba(0, 255, 255, 0.3);
      border-radius: 50%;
      box-shadow: 0 0 15px rgba(0, 255, 255, 0.8);
    }
  `}
  
  ${props => props.position === 'bottom-left' && css`
    bottom: -2px;
    left: -2px;
    border-width: 0 0 3px 3px;
    animation-delay: 1.5s;
    
    &::before {
      content: '';
      position: absolute;
      bottom: -5px;
      left: -5px;
      width: 15px;
      height: 15px;
      background: rgba(0, 255, 255, 0.3);
      border-radius: 50%;
      box-shadow: 0 0 15px rgba(0, 255, 255, 0.8);
    }
  `}
  
  ${props => props.position === 'bottom-right' && css`
    bottom: -2px;
    right: -2px;
    border-width: 0 3px 3px 0;
    animation-delay: 2.25s;
    
    &::before {
      content: '';
      position: absolute;
      bottom: -5px;
      right: -5px;
      width: 15px;
      height: 15px;
      background: rgba(0, 255, 255, 0.3);
      border-radius: 50%;
      box-shadow: 0 0 15px rgba(0, 255, 255, 0.8);
    }
  `}
`;

const DataFlow = styled.div`
  position: absolute;
  height: 3px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 255, 255, 0.8),
    rgba(100, 182, 255, 1),
    rgba(0, 255, 255, 0.8),
    transparent
  );
  width: 350px;
  box-shadow: 0 0 10px rgba(0, 255, 255, 0.6);
  filter: blur(0.5px);
  
  ${props => props.position === 'top' && css`
    top: 30%;
    left: 0;
    animation: ${dataFlowLeft} 5s linear infinite;
    animation-delay: ${props.delay}s;
  `}
  
  ${props => props.position === 'bottom' && css`
    bottom: 30%;
    right: 0;
    animation: ${dataFlowRight} 5s linear infinite;
    animation-delay: ${props.delay}s;
  `}
`;

const MainTitle = styled.h1`
  font-family: 'Orbitron', sans-serif;
  font-size: 4.5rem;
  font-weight: 200;
  background: linear-gradient(
    135deg,
    #ffffff 0%,
    #00FFFF 15%,
    #64B6FF 35%,
    #00FFFF 55%,
    #ffffff 70%,
    #64B6FF 85%,
    #00FFFF 100%
  );
  background-size: 400% 400%;
  animation: 
    ${css`${gradientShift}`} 8s ease infinite, 
    ${css`${perspectivePulse}`} 12s ease-in-out infinite, 
    ${css`${hologramFlicker}`} 3s ease-in-out infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-align: center;
  letter-spacing: 18px;
  text-transform: uppercase;
  position: relative;
  filter: 
    drop-shadow(0 0 80px rgba(0, 255, 255, 0.8))
    drop-shadow(0 0 140px rgba(0, 255, 255, 0.5))
    drop-shadow(0 0 220px rgba(100, 182, 255, 0.3))
    drop-shadow(0 15px 60px rgba(0, 0, 0, 0.8));
  transform-style: preserve-3d;
  opacity: 0.98;
  margin: 0;
  
  &::before {
    content: 'ANÚNCIOS';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background: linear-gradient(
      135deg,
      rgba(0, 255, 255, 0.5),
      rgba(100, 182, 255, 0.5)
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    z-index: -1;
    filter: blur(30px);
    transform: translateZ(-80px);
    animation: ${css`${quantumPulse}`} 5s ease-in-out infinite;
  }
  
  &::after {
    content: 'ANÚNCIOS';
    position: absolute;
    top: 8px;
    left: 8px;
    right: 0;
    color: rgba(0, 255, 255, 0.2);
    z-index: -2;
    filter: blur(8px);
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    letter-spacing: 4px;
  }
`;

const PresentationSlide12A = () => {
  const { lang } = useLanguage();
  const t = getTranslation('slide12a', lang);
  const particles = [
    { size: 4, color: 'rgba(100, 182, 255, 0.6)', top: 15, left: 20, duration: 6, delay: 0 },
    { size: 3, color: 'rgba(0, 255, 255, 0.6)', top: 30, left: 80, duration: 7, delay: 1 },
    { size: 5, color: 'rgba(100, 182, 255, 0.5)', top: 70, left: 15, duration: 8, delay: 2 },
    { size: 4, color: 'rgba(0, 255, 255, 0.5)', top: 85, left: 85, duration: 6, delay: 1.5 },
    { size: 3, color: 'rgba(100, 182, 255, 0.6)', top: 50, left: 50, duration: 7, delay: 0.5 },
    { size: 4, color: 'rgba(0, 255, 255, 0.5)', top: 25, left: 40, duration: 9, delay: 3 },
  ];

  const hexagons = [
    { size: 60, color: 'rgba(100, 182, 255, 0.3)', top: 10, left: 10, duration: 12, delay: 0 },
    { size: 80, color: 'rgba(0, 255, 255, 0.2)', top: 20, left: 85, duration: 15, delay: 2 },
    { size: 70, color: 'rgba(100, 182, 255, 0.25)', top: 80, left: 12, duration: 13, delay: 4 },
    { size: 65, color: 'rgba(0, 255, 255, 0.3)', top: 75, left: 88, duration: 14, delay: 1 },
  ];

  return (
    <SlideWrapper>
      <TechGrid />
      <Scanline />
      <EnergyRing />
      
      <HexagonLayer>
        {hexagons.map((hexagon, index) => (
          <Hexagon key={`hex-${index}`} {...hexagon} />
        ))}
      </HexagonLayer>
      
      <FloatingParticles>
        {particles.map((particle, index) => (
          <Particle key={`particle-${index}`} {...particle} />
        ))}
      </FloatingParticles>
      
      <ContentPreview>
        <Corner position="top-left" />
        <Corner position="top-right" />
        <Corner position="bottom-left" />
        <Corner position="bottom-right" />
        
        <DataFlow position="top" delay={0} />
        <DataFlow position="bottom" delay={2} />
        
        <MainTitle>{t.title}</MainTitle>
      </ContentPreview>
    </SlideWrapper>
  );
};

export default PresentationSlide12A;
